import type {ImageField, LinkField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import cn from 'classnames'
import Link from 'next/link'
import {useRouter} from 'next/router'
import {useLinkResolver} from 'prismicio'
import {getLinkLocale} from 'src/common/helpers'
import {useTranslate} from 'src/common/i18n/useTranslate'
import {Icon} from 'src/common/Icon'
import {DEFAULT_PADDING, PADDING_BOTTOM_CLASS, PADDING_TOP_CLASS} from 'src/common/paddingClasses'

export type CustomerTile = {
  image: ImageField
  authorName: string
  authorRole: string
  companyName: string
  quote: string
  link: LinkField
}

export type CustomerTiles3Props = {
  sliceType: string
  sliceVariation: string
  tiles: CustomerTile[]
  paddingTop?: string | null
  paddingBottom?: string | null
}

const CustomerTileCard = ({image, authorName, authorRole, companyName, quote, link}: CustomerTile) => {
  const {linkResolver} = useLinkResolver()
  const {locale} = useRouter()
  const translate = useTranslate()
  const roleLine = [authorRole, companyName].filter(Boolean)
  const linkLabel = [authorName, companyName].filter(Boolean).join(', ')

  return (
    <Link
      href={linkResolver(link, 'customer-tiles-3')}
      locale={getLinkLocale(link, locale)}
      aria-label={
        linkLabel
          ? translate('customerTiles3.readStoryNamed', {name: linkLabel})
          : translate('customerTiles3.readStory')
      }
      className="group relative flex h-[400px] w-[85%] shrink-0 snap-start flex-col justify-end overflow-hidden rounded-lg border border-moss-white/10 bg-green-deep transition-[height] duration-500 ease-out md:w-auto md:hover:h-[440px] md:focus-visible:h-[440px]"
    >
      {isFilled.image(image) && <PrismicNextImage field={image} fill sizes="760px" className="object-cover" />}
      <div className="absolute inset-0 bg-gradient-to-t from-green-deep/90 via-green-deep/20 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-green-deep via-green-deep/70 to-transparent transition-opacity duration-500 md:opacity-0 md:group-hover:opacity-100 md:group-focus-visible:opacity-100" />
      <span className="absolute right-3 top-3 z-10 flex size-7 items-center justify-center rounded-[3px] bg-moss-white/[0.05] backdrop-blur-sm transition-colors duration-300 group-hover:bg-moss-white/[0.25]">
        <Icon name="arrow" className="h-2 w-2.5 text-moss-white" aria-hidden="true" />
      </span>
      <div className="relative z-10 p-5">
        {quote && (
          <div className="grid grid-rows-[1fr] transition-[grid-template-rows] duration-500 ease-out md:grid-rows-[0fr] md:group-hover:grid-rows-[1fr] md:group-focus-visible:grid-rows-[1fr]">
            <div className="overflow-hidden">
              <p className="pb-6 font-main text-16 leading-snug text-moss-white transition-opacity duration-500 md:opacity-0 md:group-hover:opacity-100 md:group-focus-visible:opacity-100">
                {quote}
              </p>
            </div>
          </div>
        )}
        {authorName && <p className="font-main text-18 leading-tight text-moss-white">{authorName}</p>}
        {roleLine.length > 0 && (
          <p className="mt-2 flex items-center gap-2 font-main text-12 uppercase tracking-[1px] text-moss-white/70">
            {roleLine.map((part, partIndex) => (
              <span key={part} className="flex items-center gap-2">
                {partIndex > 0 && <span className="size-1 bg-moss-white/70" aria-hidden="true" />}
                {part}
              </span>
            ))}
          </p>
        )}
      </div>
    </Link>
  )
}

export const CustomerTiles3 = ({sliceType, sliceVariation, tiles, paddingTop, paddingBottom}: CustomerTiles3Props) => {
  if (tiles.length === 0) {
    return null
  }

  return (
    <section
      data-slice-type={sliceType}
      data-slice-variation={sliceVariation}
      data-nav-dark="true"
      className={cn(
        'bg-green-dark md:px-10 lg:px-5 xl:px-0',
        PADDING_TOP_CLASS[paddingTop ?? DEFAULT_PADDING],
        PADDING_BOTTOM_CLASS[paddingBottom ?? DEFAULT_PADDING],
      )}
    >
      <div className="mx-auto flex max-w-container snap-x snap-mandatory scroll-pl-5 items-start gap-5 overflow-x-auto px-5 md:grid md:snap-none md:grid-cols-2 md:overflow-visible md:px-0 lg:grid-cols-3">
        {tiles.map((tile, tileIndex) => (
          <CustomerTileCard
            key={tileIndex}
            image={tile.image}
            authorName={tile.authorName}
            authorRole={tile.authorRole}
            companyName={tile.companyName}
            quote={tile.quote}
            link={tile.link}
          />
        ))}
      </div>
    </section>
  )
}
